import { Fragment } from "react";

import cn from "classnames";

import { Text } from "@/components/text/text.component";

import type { AppRatingsComparisonTable as AppRatingsComparisonTableData } from "@/sanity.types";

import {
  desktopColumnHighlightClass,
  RATING_VALUE_TEXT_CLASS,
  rowDividerClass,
} from "./appRatingsComparisonTable.tableUtils";
import type { ComparisonColumn } from "./appRatingsComparisonTable.types";
import { BrandHeaderGradientRule } from "./brandHeaderGradientRule.component";
import { ColumnHeader } from "./columnHeader.component";

export function DesktopRatingsTable({
  columns,
  rows,
  firstColumnHeader,
  highlightIndex,
}: DesktopRatingTableProps) {
  const gridTemplateColumns = `minmax(128px, 1.25fr) repeat(${columns.length}, minmax(72px, 1fr))`;

  return (
    <div
      className="hidden overflow-x-auto pb-1 lg:block"
      role="table"
      aria-label="See how we compare to our competitors">
      <div
        role="row"
        className="grid min-w-[min(100%,520px)] sm:min-w-0"
        style={{ gridTemplateColumns }}>
        <div
          className="text-greyscale-body flex min-h-[4.5rem] items-end pb-5 sm:min-h-[5.5rem]"
          role="rowheader">
          {firstColumnHeader ? (
            <Text
              variant="body2"
              component="span"
              className="font-medium"
              role="cell">
              {firstColumnHeader}
            </Text>
          ) : (
            <span className="sr-only" role="cell">
              Labels
            </span>
          )}
        </div>

        {columns.map((col, columnIndex) => (
          <div
            key={col._key}
            role="columnheader"
            className={cn(
              "flex min-h-[4.5rem] flex-col items-stretch justify-end pb-5 sm:min-h-[5.5rem]",
              desktopColumnHighlightClass(
                columnIndex,
                highlightIndex,
                "header",
              ),
            )}>
            <ColumnHeader column={col as ComparisonColumn} />
          </div>
        ))}

        <BrandHeaderGradientRule />

        {rows.map((row, rowIndex) => {
          const isLastRow = rowIndex === rows.length - 1;
          const isFirstBodyRow = rowIndex === 0;

          return (
            <Fragment key={row._key}>
              <div
                role="rowheader"
                className={cn(
                  "text-greyscale-body flex items-center py-5 sm:py-6",
                  rowDividerClass(isFirstBodyRow),
                  isFirstBodyRow && "pt-6 sm:pt-8",
                )}>
                <Text variant="body2" component="span" role="cell">
                  {row.rowLabel}
                </Text>
              </div>
              {columns.map((col, columnIndex) => (
                <div
                  role="cell"
                  key={`${row._key}-${col._key}`}
                  className={cn(
                    "text-greyscale-body flex items-center justify-center py-5 text-center sm:py-6",
                    rowDividerClass(isFirstBodyRow),
                    isFirstBodyRow && "pt-6 sm:pt-8",
                    desktopColumnHighlightClass(
                      columnIndex,
                      highlightIndex,
                      isLastRow ? "last" : "body",
                    ),
                  )}>
                  <Text
                    variant="body1"
                    component="span"
                    className={RATING_VALUE_TEXT_CLASS}>
                    {row.values?.[columnIndex] ?? "—"}
                  </Text>
                </div>
              ))}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

type DesktopRatingTableProps = {
  columns: NonNullable<AppRatingsComparisonTableData["columns"]>;
  rows: NonNullable<AppRatingsComparisonTableData["rows"]>;
  firstColumnHeader: string | null | undefined;
  highlightIndex: number;
};
